/**
 * Diário do "Jarvis" (spec 011): quantas vezes abriu, foi cancelado ou
 * marcado "não chamei" — é o número que diz se a frase dispara sozinha. O
 * "Atlas" (comandos do sistema, 03/10/2026) conta à parte, com os pedidos.
 *
 * Também: horas escutando, grafias do nome e reinícios do reconhecedor.
 *
 * **Nenhum texto entra aqui.** Nem o que foi ouvido, nem paciente: só
 * contagens e a grafia do NOME ("jarvis", "jarbas"). Fica no localStorage da
 * máquina, separado do diário do ditado (`fieldLog`), pra não mexer no
 * contador das 20 sessões da spec 009.
 */

import type { WakeMatch } from './matcher';
import { isFullWake } from './matcher';

const LS_KEY = 'dratento.voice.wakelog.v1';
const MAX_ULTIMOS = 30;

export type WakeEventKind = 'frase' | 'frase-sem-comando' | 'nome' | 'quase' | 'atlas';

export interface WakeLog {
  /** Primeiro registro (ms). 0 = vazio. */
  desde: number;
  /** Tempo total escutando (ms). */
  armadoMs: number;
  /** Frase que abre o ditado ("Jarvis"). */
  frases: number;
  /** "Jarvis, cancela" (não abre). */
  frasesSemComando: number;
  /** "Jarvis" sem o "ok". */
  nomeSolto: number;
  /** Grafia parecida, não aceita (calibração). */
  quase: number;
  /** "Atlas" ouvido — o nome dos comandos do sistema (conta desde 03/10/2026). */
  atlas: number;
  variantes: Record<string, number>;
  comandos: Record<string, number>;
  /** Sessões do reconhecedor que terminaram e foram religadas. */
  reinicios: number;
  erros: Record<string, number>;
  /** O que aconteceu depois da frase. */
  abriu: number;
  /**
   * O ao vivo aberto pelo "Jarvis" chegou a ouvir (conta desde 27/09/2026).
   * `abriu` bem maior que isto = a palavra é ouvida mas o ao vivo não liga.
   */
  aoVivo: number;
  canceladas: number;
  naoChamei: number;
  ultimos: Array<{ at: number; tipo: WakeEventKind; variante: string; comando: string | null }>;
}

const vazio = (): WakeLog => ({
  desde: 0,
  armadoMs: 0,
  frases: 0,
  frasesSemComando: 0,
  nomeSolto: 0,
  quase: 0,
  atlas: 0,
  variantes: {},
  comandos: {},
  reinicios: 0,
  erros: {},
  abriu: 0,
  aoVivo: 0,
  canceladas: 0,
  naoChamei: 0,
  ultimos: [],
});

export function getWakeLog(): WakeLog {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return vazio();
    return { ...vazio(), ...(JSON.parse(raw) as Partial<WakeLog>) };
  } catch {
    return vazio();
  }
}

function update(fn: (log: WakeLog) => void): void {
  const log = getWakeLog();
  if (!log.desde) log.desde = Date.now();
  fn(log);
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(log));
  } catch {
    /* storage cheio/bloqueado — o diário é conveniência */
  }
}

export function wakeEventKind(m: WakeMatch): WakeEventKind {
  if (m.nome === 'atlas') return 'atlas';
  if (m.quase) return 'quase';
  if (isFullWake(m)) return 'frase';
  return m.comOk ? 'frase-sem-comando' : 'nome';
}

export function recordWakeMatch(m: WakeMatch): WakeEventKind {
  const tipo = wakeEventKind(m);
  update((log) => {
    if (tipo === 'frase') log.frases += 1;
    else if (tipo === 'frase-sem-comando') log.frasesSemComando += 1;
    else if (tipo === 'nome') log.nomeSolto += 1;
    else if (tipo === 'atlas') log.atlas += 1;
    else log.quase += 1;
    log.variantes[m.variante] = (log.variantes[m.variante] ?? 0) + 1;
    if (m.comando) log.comandos[m.comando] = (log.comandos[m.comando] ?? 0) + 1;
    log.ultimos = [...log.ultimos, { at: Date.now(), tipo, variante: m.variante, comando: m.comando }].slice(-MAX_ULTIMOS);
  });
  return tipo;
}

export function recordWakeArmed(ms: number): void {
  if (ms <= 0) return;
  update((log) => {
    log.armadoMs += ms;
  });
}

export function recordWakeRestart(): void {
  update((log) => {
    log.reinicios += 1;
  });
}

/** "Atlas" sozinho e o pedido veio na frase seguinte: conta o pedido. */
export function recordWakeCommand(comando: string): void {
  update((log) => {
    log.comandos[comando] = (log.comandos[comando] ?? 0) + 1;
  });
}

export function recordWakeError(code: string): void {
  update((log) => {
    log.erros[code] = (log.erros[code] ?? 0) + 1;
  });
}

export function recordWakeOutcome(o: 'abriu' | 'ao-vivo' | 'cancelada' | 'nao-chamei'): void {
  update((log) => {
    if (o === 'abriu') log.abriu += 1;
    else if (o === 'ao-vivo') log.aoVivo += 1;
    else if (o === 'cancelada') log.canceladas += 1;
    else log.naoChamei += 1;
  });
}

export function clearWakeLog(): void {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
}
